import React, { Component } from 'react';
import TwoColumnTable from '../TwoColumnTable';
import ContentOutline from '../ContentOutline';
import TextBlock from '../TextBlock';

import sharedStyles from '../sharedStyles.scss';

const EXAM_SUMMARY = [
  {
    topic: 'Verbal Reasoning',
    description: (
      <div>
        <p>The Verbal Reasoning section measures your ability to:</p>
        <ul>
          <li>
            analyze and draw conclusions from discourse; reason from incomplete data;
            identify author&apos;s assumptions and/or perspective; understand multiple
            levels of meaning, such as literal, figurative and author&apos;s intent
          </li>
          <li>
            select important points; distinguish major from minor or irrelevant points;
            summarize text; understand the structure of a text
          </li>
          <li>
            understand the meanings of words, sentences and entire texts; understand
            relationships among words and among concepts
          </li>
        </ul>
      </div>
    ),
    weight: 'Two sections, 20 questions each',
  },
  {
    topic: 'Quantitative Reasoning',
    description: (
      <div>
        <p>The Quantitative Reasoning section measures your ability to:</p>
        <ul>
          <li>understand, interpret and analyze quantitative information</li>
          <li>solve problems using mathematical models</li>
          <li>
            apply basic skills and elementary concepts of arithmetic, algebra, geometry
            and data analysis
          </li>
        </ul>
      </div>
    ),
    weight: 'Two sections, 20 questions each',
  },
  {
    topic: 'Analytical Writing',
    description: (
      <div>
        <p>The Analytical Writing section measures your ability to:</p>
        <ul>
          <li>articulate complex ideas clearly and effectively</li>
          <li>support ideas with relevant reasons and examples</li>
          <li>examine claims and accompanying evidence</li>
          <li>sustain a well-focused, coherent discussion</li>
          <li>control the elements of standard written English</li>
        </ul>
      </div>
    ),
    weight: 'Two essay prompts',
  },
];

const GRE_STRUCTURE = [
  {
    topic: 'Analytical Writing',
    description: (
      <div>
        <p>Two essays prompts, 30min each</p>
        <ul>
          <li>&quot;Analyze an Issue&quot; task</li>
          <li>&quot;Analyze an Argument&quot; task</li>
        </ul>
      </div>
    ),
  },
  {
    topic: 'Verbal Reasoning',
    description: (
      <div>
        <p>Two sections, 30min each</p>
        <ul>
          <li>20 questions per section</li>
        </ul>
      </div>
    ),
  },
  {
    topic: 'Quantitative Reasoning',
    description: (
      <div>
        <p>Two sections, 35min each</p>
        <ul>
          <li>20 questions per section</li>
        </ul>
      </div>
    ),
  },
  {
    topic: 'Research',
    description: (
      <div>
        <p>One section, 35min</p>
        <ul>
          <li>Either Verbal or Quantitative Reasoning</li>
          <li>20 questions</li>
        </ul>
      </div>
    ),
  },
];

class GRE extends Component {
  render() {
    const { currentUser, product, inflatedContent } = this.props;

    return (
      <div className={sharedStyles.layout}>
        <div className={sharedStyles.title}>
          Graduate Record Examination{' '}
          <span className={sharedStyles.shortTitle}>(GRE)</span>
        </div>
        <div className={sharedStyles.explanation}>
          <div className={sharedStyles.blurb}>
            The GRE is a standardized test for all types of graduate schools, including
            business and law schools. It is created and administered by Educational
            Testing Service (ETS).
          </div>
          <div className={sharedStyles.facts}>
            <ul>
              <li>
                <em>Time</em> 3 hours and 45 minutes
              </li>
              <li>
                <em>Format</em> 100 questions, 2 essays
              </li>
              <li>
                <em>Exam fee</em> $205
              </li>
              <li>
                <em>Details</em>{' '}
                <a
                  href="https://www.ets.org/gre/revised_general/about/content/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ETS <i className="fad fa-external-link" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <TwoColumnTable title="ETS's exam summary" records={EXAM_SUMMARY} />

        <ContentOutline
          currentUser={currentUser}
          product={product}
          inflatedContent={inflatedContent}
        />

        <TextBlock title="Structure of the computer-delivered test">
          <TwoColumnTable records={GRE_STRUCTURE} />
          <br />
          <p>
            The Analytical Writing section will always be first. The first 20-question
            section will be randomly determined; however, the subsequent sections will
            alternate thereafter. So whatever section you see first, you&apos;ll get three
            of. The unscored research section will be one of those three sections.
          </p>
          <p>
            The GRE is an adaptive test. This means that it changes in response to student
            performance. Unlike the GMAT (which is adaptive by question), the GRE is
            adaptive by section. As a result the number of questions you answer correctly
            on your first graded verbal or quant section will determine how difficult your
            second graded verbal or quant section will be. Provoking harder second
            sections is rewarded with &apos;free&apos; scaled points and access to the
            top-percentile scores.
          </p>
        </TextBlock>

        <TextBlock title="Paper-delivered test and testing from home">
          <p>
            While ETS does mention a paper-based test taking option for the GRE, unless
            you live in an extremely remote part of the world or you have an explicit
            accommodation from ETS, you should expect to sit for the computer-based exam.
            And while this has historically meant that students must take the GRE at a
            secure testing center, ETS recently relaxed its regulations, allowing students
            to sit for the exam at home. This process involves certain technical
            requirements and engaging a virtual proctor; however, this change gives
            students an unprecedented amount of control over where and when they take the
            test.
          </p>
        </TextBlock>
      </div>
    );
  }
}

export default GRE;
